import { useState, useCallback, useRef } from "react";

export function useCamera(videoRef) {
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("user");
  const streamRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [videoRef]);

  const startStream = useCallback(async (facingMode) => {
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: 640, height: 480 },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = resolve;
        });
        videoRef.current.play().catch((e) => console.error("Video play failed", e));
      }
      setIsCameraOn(true);
      setError(null);
    } catch (err) {
      console.error("Camera access denied:", err);
      setError("Camera access denied. Please allow camera permissions in your browser settings.");
      setIsCameraOn(false);
    }
  }, [stopStream, videoRef]);

  const endCall = useCallback(() => {
    stopStream();
    setIsCameraOn(false);
  }, [stopStream]);

  const toggleCamera = useCallback(async () => {
    if (isCameraOn) {
      endCall();
    } else {
      await startStream(facing);
    }
  }, [isCameraOn, endCall, startStream, facing]);

  const flipCamera = useCallback(async () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    if (isCameraOn) {
      await startStream(next);
    }
  }, [facing, isCameraOn, startStream]);

  return { isCameraOn, error, facing, toggleCamera, endCall, flipCamera };
}
